import { useEffect, useState } from "react";
import { FlaskConical, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ReadOnlyTable } from "@/components/ui/read-only-table";
import { backupDrillsApi, type BackupDrill } from "@/lib/backupDrills.api";
import type { Backup } from "@/lib/backups.api";
import { getApiErrorMessage } from "../settingsPage.helpers";
import { formatDateTime } from "./backups.helpers";
import { createRestoreDrillColumns } from "./restoreDrillColumns";

interface RestoreDrillPanelProps {
  backups: Backup[];
}

const RESTORABLE_STATUS = "success";

// C1 — restore drill: pick a backup, enter the decrypt key by hand (never
// read from the server's own env for this — see backups.service.ts), launch
// a pg_restore into a throwaway scratch database, verify it, tear it down.
// Never touches the live/connected production database.
export function RestoreDrillPanel({ backups }: RestoreDrillPanelProps) {
  const [drills, setDrills] = useState<BackupDrill[]>([]);
  const [loading, setLoading] = useState(true);
  const [backupId, setBackupId] = useState<string>("");
  const [decryptKey, setDecryptKey] = useState("");
  const [running, setRunning] = useState(false);

  const restorableBackups = backups.filter((b) => b.status === RESTORABLE_STATUS && b.filePath);

  async function loadDrills() {
    try {
      const response = await backupDrillsApi.list();
      setDrills(response.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Impossible de charger l'historique des tests."));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadDrills();
  }, []);

  async function handleRun() {
    if (!backupId || !decryptKey) return;
    setRunning(true);
    try {
      const response = await backupDrillsApi.run(Number(backupId), decryptKey);
      if (response.data.status === "failed") {
        toast.error(response.data.errorMessage ?? "Le test de restauration a échoué.");
      } else {
        toast.success("Test de restauration réussi — la base temporaire a été vérifiée puis supprimée.");
      }
      setDecryptKey("");
      await loadDrills();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Impossible de lancer le test de restauration."));
    } finally {
      setRunning(false);
    }
  }

  const columns = createRestoreDrillColumns();

  return (
    <section className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="border-b border-border px-4 py-3">
        <h3 className="text-sm font-semibold text-foreground">Test de restauration (drill)</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Restaure une sauvegarde dans une base temporaire isolée pour vérifier qu'elle est
          exploitable, puis la supprime. Ne touche jamais la base de production connectée.
        </p>
      </div>

      <div className="grid gap-3 px-4 py-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-end">
        <div>
          <label className="mb-1 block text-[11px] font-medium text-body">Sauvegarde</label>
          <Select value={backupId} onValueChange={setBackupId}>
            <SelectTrigger>
              <SelectValue placeholder="Choisir une sauvegarde" />
            </SelectTrigger>
            <SelectContent>
              {restorableBackups.map((backup) => (
                <SelectItem key={backup.id} value={String(backup.id)}>
                  {formatDateTime(backup.startedAt)} — {backup.tier}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-medium text-body">
            Clé de déchiffrement
          </label>
          <Input
            type="password"
            value={decryptKey}
            onChange={(event) => setDecryptKey(event.target.value)}
            placeholder="Clé base64"
          />
        </div>
        <Button size="sm" onClick={handleRun} disabled={running || !backupId || !decryptKey}>
          {running ? <Loader2 size={13} className="animate-spin" /> : <FlaskConical size={13} />}
          Lancer le test
        </Button>
      </div>

      <ReadOnlyTable
        bare
        columns={columns}
        data={drills}
        emptyMessage={loading ? "Chargement..." : "Aucun test de restauration pour le moment."}
      />
    </section>
  );
}
